import { env } from '@/lib/env'

import type { AdyenEnvironment } from '@/lib/types'

interface ApplePayMerchantSessionRequest {
  adyenEnvironment: AdyenEnvironment
  /** Host Safari validates the session against - the top-level page's host. */
  domainName: string
  displayName: string
  merchantIdentifier: string
}

/**
 * Requests an Apple Pay merchant session from Adyen with an explicit
 * `domainName`, replacing the SDK's own `validateMerchant` (which posts the
 * iframe's hostname). Returns the decoded session, or throws.
 * See specs/app/index.md::Apple Pay.
 */
export async function fetchApplePayMerchantSession(request: ApplePayMerchantSessionRequest): Promise<unknown> {
  const { adyenEnvironment, domainName, displayName, merchantIdentifier } = request

  const url = `https://checkoutshopper-${adyenEnvironment}.adyen.com/checkoutshopper/v1/applePay/sessions?clientKey=${env.ADYEN_CLIENT_KEY}`

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      displayName,
      domainName,
      initiative: 'web',
      merchantIdentifier,
    }),
  })

  if (!response.ok) {
    const body = await response.text().catch(() => '<unreadable>')
    throw new Error(
      `Apple Pay merchant session request failed - status: ${response.status}, domainName: ${domainName}, body: ${body}`
    )
  }

  const { data } = (await response.json()) as { data: string }
  const decodedBytes = Uint8Array.from(atob(data), (c) => c.charCodeAt(0))

  return JSON.parse(new TextDecoder().decode(decodedBytes))
}
